import { create } from "zustand";
import { isListed } from "./registry";
import { isActiveStatus, isStaleActive, resourceKey } from "./types";
import type {
  ResourceStatus,
  StoredNotification,
  UserNotification,
} from "./types";
import { tabInBackground } from "./visibility";
// Kinds register on import; pulling them in here (not only from the bell)
// means presentation is right wherever the store is first used.
import "./kinds";

const MAX_NOTIFICATIONS = 100;
/** Cleared ids kept for the life of the tab; a reload is the server's job. */
const MAX_CLEARED = 500;

/**
 * History, not news: the backend backfills the recent entries of the user
 * stream (tagged `_replay`) to every fresh subscriber, which is what keeps
 * the bell across a reload without a database. History lands in the bell
 * read — unless the server tags it `_unseen`, meaning it finished while the
 * user had no tab open, in which case it is unread — and updates row status,
 * but never toasts, chimes or pops an OS notification. Events older than
 * this at subscribe time count as history
 * too, in case the tag is missing (older backend); generous so clock skew
 * between the server and a plant workstation can't silence a real alert.
 */
export const REPLAY_TOLERANCE_MS = 60 * 1000;

const countUnread = (list: StoredNotification[]) =>
  list.reduce((n, x) => n + (x.read ? 0 : 1), 0);

/**
 * True when the user is looking at the notification's resource right now:
 * it is on screen *and* this tab is focused. A background tab is not
 * looking at anything, so its resources still count as unseen.
 */
export const isOnScreen = (
  viewing: Record<string, true>,
  n: UserNotification,
): boolean =>
  !!n.resource_type &&
  !!n.resource_id &&
  !!viewing[resourceKey(n.resource_type, n.resource_id)] &&
  !tabInBackground();

interface UserNotificationState {
  /** Newest first; at most one entry per resource. */
  notifications: StoredNotification[];
  /** Entries not yet read — drives the badge, title and favicon. */
  unreadCount: number;
  /** Latest status per `${resource_type}:${resource_id}`; absent means idle. */
  resourceStatus: Record<string, ResourceStatus>;
  /** Resources currently on screen (see isOnScreen). */
  viewing: Record<string, true>;
  /**
   * Listed notifications that have not been delivered (toast / sound /
   * desktop) yet, oldest first. A queue rather than "the latest one" so a
   * batch — two chats finishing together, a Last-Event-ID backlog after a
   * network blip — delivers every alert, not just the last.
   */
  pending: UserNotification[];
  /** When this tab subscribed; events from before it are history. */
  subscribedAt: number | null;
  /**
   * Ids the user cleared or dismissed in this tab. The backend republishes
   * a completed turn under the same id once its digest is ready, seconds
   * later; that update must not bring a cleared alert back. Across a reload
   * the server's cleared-through cursor does the same job (see api.ts).
   */
  cleared: Record<string, true>;
  /**
   * Bumped whenever an entry becomes read by the user's doing — clicking
   * it, Mark all read, opening the chat, or the chat being on screen when
   * the alert arrives. A wipe (reset, Clear all) is not a read.
   */
  readVersion: number;

  ingest: (n: UserNotification) => void;
  /** Take everything awaiting delivery. */
  drainPending: () => UserNotification[];
  markRead: (id: string) => void;
  markAllRead: () => void;
  dismiss: (id: string) => void;
  clearAll: () => void;
  setViewing: (type: string, id: string | number, viewing: boolean) => void;
  /** The user has looked at a resource: its status goes back to idle and
   *  its bell entries are read. */
  markResourceSeen: (type: string, id: string | number) => void;
  setSubscribedAt: (at: number | null) => void;
  /** Forget everything — on logout and when the signed-in user changes. */
  reset: () => void;
}

const EMPTY = {
  notifications: [] as StoredNotification[],
  unreadCount: 0,
  resourceStatus: {} as Record<string, ResourceStatus>,
  viewing: {} as Record<string, true>,
  pending: [] as UserNotification[],
  subscribedAt: null as number | null,
  cleared: {} as Record<string, true>,
  readVersion: 0,
};

export const useUserNotificationStore = create<UserNotificationState>()(
  (set, get) => ({
    ...EMPTY,

    ingest: (n) => {
      const state = get();
      if (state.cleared[n.id]) return;
      const existing = state.notifications.find((x) => x.id === n.id);
      if (existing) {
        // An update to something we already hold (e.g. the digest replacing
        // the excerpt): refresh the entry in place, keep read state, and
        // don't alert again.
        const notifications = state.notifications.map((x) =>
          x.id === n.id ? { ...x, ...n, read: x.read } : x,
        );
        // Refresh the row status only if it is still showing; an update
        // must not bring back a dot the user has already cleared by
        // opening the chat.
        const key =
          n.resource_type && n.resource_id
            ? resourceKey(n.resource_type, n.resource_id)
            : null;
        set({
          notifications,
          resourceStatus:
            key && key in state.resourceStatus
              ? withStatus(state.resourceStatus, n, false)
              : state.resourceStatus,
        });
        return;
      }

      const onScreen = isOnScreen(state.viewing, n);
      const listed = isListed(n);
      const isHistory =
        n._replay === true ||
        (state.subscribedAt !== null &&
          Date.parse(n.created_at) < state.subscribedAt - REPLAY_TOLERANCE_MS);
      const unseen = n._replay === true && n._unseen === true;

      let notifications = state.notifications;
      if (listed) {
        // One entry per resource: a later alert for the same chat replaces
        // the earlier one instead of stacking up.
        const sameResource = (x: StoredNotification) =>
          !!n.resource_type &&
          !!n.resource_id &&
          x.resource_type === n.resource_type &&
          x.resource_id === n.resource_id;
        notifications = [
          { ...n, read: onScreen || (isHistory && !unseen) },
          ...state.notifications.filter((x) => !sameResource(x)),
        ].slice(0, MAX_NOTIFICATIONS);
      }
      set({
        // A replayed finished status the user had a tab open for counts as
        // seen: a dot that came back on every reload for a day would be
        // noise, not news. A replayed active status still shows the spinner.
        resourceStatus: withStatus(
          state.resourceStatus,
          n,
          onScreen || (n._replay === true && !unseen),
        ),
        notifications,
        unreadCount: countUnread(notifications),
        pending: listed && !isHistory ? [...state.pending, n] : state.pending,
        readVersion:
          listed && onScreen ? state.readVersion + 1 : state.readVersion,
      });
    },

    drainPending: () => {
      const { pending } = get();
      if (pending.length) set({ pending: [] });
      return pending;
    },

    markRead: (id) =>
      set((s) => {
        if (!s.notifications.some((x) => x.id === id && !x.read)) return {};
        const notifications = s.notifications.map((x) =>
          x.id === id ? { ...x, read: true } : x,
        );
        return {
          notifications,
          unreadCount: countUnread(notifications),
          readVersion: s.readVersion + 1,
        };
      }),

    markAllRead: () =>
      set((s) => {
        if (s.unreadCount === 0) return {};
        return {
          notifications: s.notifications.map((x) =>
            x.read ? x : { ...x, read: true },
          ),
          unreadCount: 0,
          readVersion: s.readVersion + 1,
        };
      }),

    dismiss: (id) =>
      set((s) => {
        const notifications = s.notifications.filter((n) => n.id !== id);
        return {
          notifications,
          unreadCount: countUnread(notifications),
          cleared: remember(s.cleared, [id]),
        };
      }),

    clearAll: () =>
      set((s) => ({
        notifications: [],
        unreadCount: 0,
        cleared: remember(
          s.cleared,
          s.notifications.map((n) => n.id),
        ),
      })),

    setViewing: (type, id, viewing) =>
      set((s) => {
        const key = resourceKey(type, id);
        const next = { ...s.viewing };
        if (viewing) next[key] = true;
        else delete next[key];
        return { viewing: next };
      }),

    markResourceSeen: (type, id) =>
      set((s) => {
        const key = resourceKey(type, id);
        const current = s.resourceStatus[key];
        const patch: Partial<UserNotificationState> = {};
        // A finished status is cleared; a live one stays (the spinner is
        // still true) unless it's so old that nothing is coming.
        if (
          current &&
          (!isActiveStatus(current.status) || isStaleActive(current))
        ) {
          const next = { ...s.resourceStatus };
          delete next[key];
          patch.resourceStatus = next;
        }
        const notifications = s.notifications.map((x) =>
          !x.read && x.resource_type === type && x.resource_id === String(id)
            ? { ...x, read: true }
            : x,
        );
        if (notifications.some((x, i) => x !== s.notifications[i])) {
          patch.notifications = notifications;
          patch.unreadCount = countUnread(notifications);
          patch.readVersion = s.readVersion + 1;
        }
        return patch;
      }),

    setSubscribedAt: (at) => set({ subscribedAt: at }),

    reset: () => set({ ...EMPTY }),
  }),
);

/** `cleared` plus `ids`, dropping the oldest past the cap. */
function remember(
  cleared: Record<string, true>,
  ids: string[],
): Record<string, true> {
  if (ids.length === 0) return cleared;
  const next: Record<string, true> = { ...cleared };
  for (const id of ids) next[id] = true;
  const keys = Object.keys(next);
  if (keys.length <= MAX_CLEARED) return next;
  return Object.fromEntries(
    keys.slice(keys.length - MAX_CLEARED).map((k) => [k, true as const]),
  );
}

function withStatus(
  resourceStatus: Record<string, ResourceStatus>,
  n: UserNotification,
  seen: boolean,
): Record<string, ResourceStatus> {
  if (!n.resource_type || !n.resource_id || !n.status) return resourceStatus;
  const key = resourceKey(n.resource_type, n.resource_id);
  const next = { ...resourceStatus };
  if (!isActiveStatus(n.status) && seen) delete next[key];
  else next[key] = { status: n.status, updatedAt: n.created_at };
  return next;
}
